import { useEffect, useState } from 'react';

import { useNavigate, useSearchParams } from 'react-router';

import { DELAYS } from '../constants';
import { AuthStatus } from '../types';
import {
  clearCompanionRetryAttempted,
  peekCompanionAuthParams,
  peekCompanionRetryAttempted,
  readAndClearCompanionAuthParams,
  setCompanionForceLogin,
  setCompanionRetryAttempted,
} from '../utils/companionDeeplink';
import {
  parseBaseAuthParams,
  parsePlatformParams,
} from '../utils/platformParsers';

import { APP_ROUTES } from '@/constants/routes';
import { useGetMeetingUrl } from '@/hooks/api/useGetMeetingUrl';
import { openMeeting } from '@/utils/meetingPlatforms';

/**
 * Completion page for the hybrid companion auth flow. The backend has already
 * delivered the token + session handle to the embedded companion over WS and
 * redirected here. This page is an authenticated SPA page (the launcher cached
 * the session), so it resolves the meeting URL the same way the old flow did —
 * directly for Zoom, or via the authenticated lookup for Teams/Webex — opens the
 * deeplink, and closes.
 */
export const useExternalAuthComplete = (): AuthStatus => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const errorParam = searchParams.get('error');
  // Retryable: a recoverable silent-auth (prompt=none) failure the backend
  // collapsed into one code, a subject mismatch, or a connection mismatch (wrong
  // Auth0 connection minted in a shared tenant). All recover identically —
  // force-login flag → relaunch with prompt=login (pinned to the right connection).
  const isRetryableAuthError =
    errorParam === 'subject_mismatch' ||
    errorParam === 'reauth_required' ||
    errorParam === 'connection_mismatch';
  // Terminal: any non-empty, non-retryable code. Covers 'auth' AND any
  // unknown/future code — the latter must NOT fall through to the success path
  // (openMeeting/window.close) as it otherwise would.
  const isError = !!errorParam && !isRetryableAuthError;

  // Peek (without clearing) to decide whether a retry is possible. Computed
  // synchronously at render time — these are pure reads. The one-shot guard
  // (retryAlreadyAttempted) caps auto-retry at once per flow.
  const retryAlreadyAttempted = peekCompanionRetryAttempted();
  const hasStashedParamsForRetry =
    isRetryableAuthError &&
    peekCompanionAuthParams() !== null &&
    !retryAlreadyAttempted;

  // Retry on a recoverable error: mark the one-shot attempt, set the force-login
  // flag, and navigate back to the launcher WITHOUT clearing stashed params (the
  // retry needs them). Guard: only retry when stashed params exist and the cap is
  // unspent — prevents an externally-injected ?error= URL or a loop from
  // triggering a spurious re-auth.
  useEffect(() => {
    if (hasStashedParamsForRetry) {
      setCompanionRetryAttempted();
      setCompanionForceLogin();
      navigate(APP_ROUTES.EXTERNAL_AUTH, { replace: true });
    }
  }, [hasStashedParamsForRetry, navigate]);

  // Read (and clear) stashed params exactly once on mount.
  // useState initializer (not useMemo) — React Compiler may re-run useMemo
  // bodies; useState initializers are guaranteed to run once per mount.
  // When retrying, leave params intact so the launcher can read them.
  const [stashedParams] = useState(() =>
    isRetryableAuthError ? null : readAndClearCompanionAuthParams(),
  );

  const baseParams = stashedParams ? parseBaseAuthParams(stashedParams) : null;
  const platformParams =
    stashedParams && baseParams
      ? parsePlatformParams(stashedParams, baseParams.platform)
      : null;

  const { data: resolvedUrl, isLoading: isMeetingUrlLoading } =
    useGetMeetingUrl(platformParams);
  const finalMeetingUrl = baseParams?.meetingUrl || resolvedUrl || '';

  const [done, setDone] = useState(false);

  // Retryable code but no retry will fire — either no stashed params
  // (externally-injected URL / already consumed) OR the one-shot cap is already
  // spent — show an error rather than spinning forever.
  const isRetryableButBlocked = isRetryableAuthError && !hasStashedParamsForRetry;
  const isAnyError = isError || isRetryableButBlocked;

  useEffect(() => {
    if (isAnyError || hasStashedParamsForRetry || isMeetingUrlLoading) {
      return;
    }

    setDone(true);
    // Flow resolved — reset the one-shot retry cap so a subsequent flow in a
    // reused tab starts clean (the launcher also resets on a fresh launch).
    clearCompanionRetryAttempted();

    if (finalMeetingUrl && baseParams?.platform) {
      const timer = setTimeout(() => {
        openMeeting(finalMeetingUrl, baseParams.platform, {
          showFallbackConfirm: false,
          onAppOpened: () => {
            setTimeout(() => window.close(), DELAYS.MEETING_REDIRECT * 3);
          },
          onFallback: () => {
            window.close();
          },
        });
      }, DELAYS.MEETING_REDIRECT);
      return () => clearTimeout(timer);
    }

    window.close();
  }, [isAnyError, hasStashedParamsForRetry, isMeetingUrlLoading, finalMeetingUrl, baseParams?.platform]);

  return {
    isLoading: !isAnyError && (hasStashedParamsForRetry || isMeetingUrlLoading || !done),
    isSuccess: done,
    isError: isAnyError,
    error: isAnyError ? new Error('Authentication failed') : undefined,
    // Manual re-login is worth offering only when the stash survived — true for the
    // reauth_required/subject_mismatch BLOCKED case (retryable path preserves the
    // stash), false for terminal errors (the stash was read-and-cleared on mount).
    canManualRetry: isAnyError && peekCompanionAuthParams() !== null,
  };
};
